/**
 * The player's own view of the board (wheel/pinch zoom, drag pan), applied
 * outside the motion camera on the root G. It persists until the next move,
 * then glides back to the auto-fit (identity); a double-tap re-fits at once.
 * Pure maths and state; the board hook wires it to input.
 */

/** screen = k * board + (x, y) */
export interface View {
  k: number;
  x: number;
  y: number;
}

export interface Bounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export const IDENTITY: View = { k: 1, x: 0, y: 0 };

export const VIEWPORT = {
  minK: 0.4,
  maxK: 4,
  /** A press that moves less than this is a tap, not a drag (px). */
  dragPx: 6,
  /** Keep at least this much of the content on screen (px). */
  keepPx: 48,
  doubleTapMs: 300,
  doubleTapPx: 24,
  /** Wheel zoom per pixel of deltaY; a trackpad pinch (ctrlKey) is finer-grained. */
  wheelRate: 0.0015,
  pinchWheelRate: 0.01,
  glideMs: 170,
};

export const isIdentity = (v: View) =>
  Math.abs(v.k - 1) < 1e-6 && Math.abs(v.x) < 1e-3 && Math.abs(v.y) < 1e-3;

export const clampZoom = (k: number) =>
  Math.max(VIEWPORT.minK, Math.min(VIEWPORT.maxK, k));

/** Zoom by `factor` keeping the screen point (px, py) fixed. */
export function zoomAbout(
  v: View,
  factor: number,
  px: number,
  py: number,
): View {
  const k = clampZoom(v.k * factor);
  const r = k / v.k;
  return { k, x: px - (px - v.x) * r, y: py - (py - v.y) * r };
}

export const panBy = (v: View, dx: number, dy: number): View => ({
  k: v.k,
  x: v.x + dx,
  y: v.y + dy,
});

/** Keep at least keepPx of the content box on screen on each axis. */
export function clampView(
  v: View,
  b: Bounds,
  size: { w: number; h: number },
  keep = VIEWPORT.keepPx,
): View {
  const axis = (t: number, lo: number, hi: number, span: number) => {
    const m = Math.min(keep, (hi - lo) * v.k, span / 2);
    const min = m - v.k * hi;
    const max = span - m - v.k * lo;
    return min > max ? (min + max) / 2 : Math.max(min, Math.min(max, t));
  };
  return {
    k: v.k,
    x: axis(v.x, b.x0, b.x1, size.w),
    y: axis(v.y, b.y0, b.y1, size.h),
  };
}

/** The zoom factor for one wheel event (deltaMode 1 = lines). */
export function wheelFactor(deltaY: number, deltaMode = 0, ctrlKey = false) {
  const px =
    deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  const rate = ctrlKey ? VIEWPORT.pinchWheelRate : VIEWPORT.wheelRate;
  return Math.exp(-px * rate);
}

export const passedDrag = (dx: number, dy: number, th = VIEWPORT.dragPx) =>
  Math.hypot(dx, dy) >= th;

type Pt = { x: number; y: number };

/** Two-finger pinch: scale by the finger spread about the midpoint, and pan with it. */
export function pinchView(start: View, a0: Pt, b0: Pt, a: Pt, b: Pt): View {
  const d0 = Math.max(1, Math.hypot(b0.x - a0.x, b0.y - a0.y));
  const d = Math.hypot(b.x - a.x, b.y - a.y);
  const m0 = { x: (a0.x + b0.x) / 2, y: (a0.y + b0.y) / 2 };
  const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  return panBy(zoomAbout(start, d / d0, m0.x, m0.y), m.x - m0.x, m.y - m0.y);
}

export interface Tap {
  t: number;
  x: number;
  y: number;
}

export const isDoubleTap = (prev: Tap | null, next: Tap) =>
  !!prev &&
  next.t - prev.t <= VIEWPORT.doubleTapMs &&
  Math.hypot(next.x - prev.x, next.y - prev.y) <= VIEWPORT.doubleTapPx;

/** The player's view, and a glide back to the fit when one is running. */
export interface ViewState {
  view: View;
  glide: { from: View; t0: number } | null;
}

export const INITIAL_VIEW: ViewState = { view: IDENTITY, glide: null };

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

/** The view at `now` (mid-glide, between the start and the fit). */
export function viewAt(s: ViewState, now: number): View {
  if (!s.glide) return s.view;
  const t = (now - s.glide.t0) / VIEWPORT.glideMs;
  if (t >= 1) return s.view;
  const e = easeOut(Math.max(0, t));
  const f = s.glide.from;
  return { k: f.k + (1 - f.k) * e, x: f.x * (1 - e), y: f.y * (1 - e) };
}

/** The player set the view (wheel, drag, pinch): it persists. */
export const setView = (v: View): ViewState => ({ view: v, glide: null });

/** A move or a double-tap: back to the fit, gliding unless reduced. */
export function refit(s: ViewState, now: number, reduced: boolean): ViewState {
  const from = viewAt(s, now);
  if (reduced || isIdentity(from)) return INITIAL_VIEW;
  return { view: IDENTITY, glide: { from, t0: now } };
}

/** The player's view composed onto the motion camera (view outside). */
export function composeCamera(
  v: View,
  cam: { k: number; dx: number; dy: number },
) {
  return { k: v.k * cam.k, dx: v.k * cam.dx + v.x, dy: v.k * cam.dy + v.y };
}
